import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Barbell, CreditCard, CalendarCheck, User, Bell, SignOut, Scan, Heartbeat,
  PersonSimpleRun, Check, PauseCircle, PlayCircle, XCircle, ArrowRight, House, UserCircle, ArrowsClockwise,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";

const iconFor = (t) => (t === "pilates" ? Heartbeat : t === "body_scan" ? Scan : t === "pt" ? PersonSimpleRun : Barbell);
const serviceImg = { body_scan: "/images/styku-results.jpeg", pt: "/images/erin.jpg" };

export default function CustomerDashboard() {
  const { user, logout, checkAuth } = useAuth();
  const [tab, setTab] = useState("overview");
  const [plans, setPlans] = useState([]);
  const [services, setServices] = useState([]);
  const [classes, setClasses] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    const [p, s, c, b, n] = await Promise.all([
      api.get("/plans"), api.get("/services"), api.get("/classes"),
      api.get("/bookings/me"), api.get("/notifications/me"),
    ]);
    setPlans(p.data); setServices(s.data); setClasses(c.data);
    setBookings(b.data); setNotifications(n.data);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const checkout = () => {
    // Payments happen on GymMaster; members sync back via the API.
    window.open("https://otorohangahf.gymmasteronline.com/portal/signup", "_blank", "noopener,noreferrer");
  };

  const bookFree = async (kind, item_id, date) => {
    try {
      await api.post("/bookings/free", { kind, item_id, date });
      toast.success("Class booked!");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const subAction = async (action) => {
    try {
      await api.post("/subscription/action", { action });
      toast.success("Subscription updated");
      await checkAuth();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const [rescheduleFor, setRescheduleFor] = useState(null);

  const cancelBooking = async (b) => {
    if (!window.confirm(`Cancel your booking for ${b.item_name}${b.date ? ` (${b.date})` : ""}?`)) return;
    try {
      await api.post(`/bookings/${b.id}/cancel`);
      toast.success("Booking cancelled");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const doReschedule = async (newClassId) => {
    try {
      await api.put(`/bookings/${rescheduleFor.id}/reschedule`, { new_class_id: newClassId });
      toast.success("Booking rescheduled");
      setRescheduleFor(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const nav = [
    { id: "overview", label: "Overview", icon: House },
    { id: "membership", label: "Membership", icon: CreditCard },
    { id: "book", label: "Book", icon: CalendarCheck },
    { id: "bookings", label: "My Bookings", icon: Barbell },
    { id: "notifications", label: "Alerts", icon: Bell, badge: notifications.filter((n) => !n.read).length },
    { id: "profile", label: "Profile", icon: User },
  ];

  const sub = user?.subscription;

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      <Sidebar nav={nav} tab={tab} setTab={setTab} logout={logout} navigate={navigate} name={user?.name} role="Member" onAccountClick={() => setTab("profile")} />
      <main className="flex-1 md:ml-64 p-6 lg:p-10 max-w-5xl">
        {tab === "overview" && (
          <Section title={`Kia ora, ${user?.name?.split(" ")[0] || "there"} 👋`} subtitle="Here's your fitness snapshot.">
            <div className="grid sm:grid-cols-3 gap-5 mb-8">
              <StatCard label="Membership" value={sub ? sub.plan_name : "None"} sub={sub ? sub.status : "Choose a plan"} highlight={sub?.status === "active"} warn={sub?.status === "overdue"} />
              <StatCard label="Bookings" value={bookings.length} sub="Total booked" />
              <StatCard label="Alerts" value={notifications.filter((n) => !n.read).length} sub="Unread" />
            </div>
            {sub?.status === "overdue" && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-5 flex items-start gap-3 mb-4" data-testid="overdue-banner">
                <XCircle size={22} weight="bold" className="text-red-500 mt-0.5" />
                <div>
                  <p className="font-bold text-red-700">Payment overdue</p>
                  <p className="text-sm text-red-600">Please renew your membership to keep your 24/7 access active.</p>
                  <button onClick={() => setTab("membership")} className="mt-2 text-sm font-bold text-red-700 underline">Renew now</button>
                </div>
              </div>
            )}
            <div className="bg-white border border-zinc-200 rounded-sm p-6">
              <h3 className="font-heading text-xl font-black mb-4">Quick actions</h3>
              <div className="flex flex-wrap gap-3">
                <ActionBtn onClick={() => setTab("book")} icon={Heartbeat}>Book Reformer Pilates</ActionBtn>
                <ActionBtn onClick={() => setTab("book")} icon={Scan}>Book Styku Scan</ActionBtn>
                <ActionBtn onClick={() => setTab("membership")} icon={CreditCard}>Manage Membership</ActionBtn>
              </div>
            </div>
          </Section>
        )}

        {tab === "membership" && (
          <Section title="Membership" subtitle="Manage or start your plan.">
            {sub ? (
              <div className="bg-white border border-zinc-200 rounded-sm p-6 mb-8" data-testid="current-sub">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Current plan</p>
                    <h3 className="font-heading text-2xl font-black">{sub.plan_name}</h3>
                    <StatusPill status={sub.status} />
                  </div>
                  <div className="flex gap-2">
                    {sub.status === "active" && (
                      <button onClick={() => subAction("pause")} data-testid="pause-sub-btn" className="inline-flex items-center gap-2 border-2 border-zinc-200 rounded-sm px-4 py-2 font-bold text-sm hover:border-zinc-950">
                        <PauseCircle size={18} /> Pause
                      </button>
                    )}
                    {sub.status === "paused" && (
                      <button onClick={() => subAction("resume")} data-testid="resume-sub-btn" className="inline-flex items-center gap-2 border-2 border-zinc-200 rounded-sm px-4 py-2 font-bold text-sm hover:border-zinc-950">
                        <PlayCircle size={18} /> Resume
                      </button>
                    )}
                    {sub.status !== "cancelled" && (
                      <button onClick={() => subAction("cancel")} data-testid="cancel-sub-btn" className="inline-flex items-center gap-2 border-2 border-red-200 text-red-600 rounded-sm px-4 py-2 font-bold text-sm hover:border-red-500">
                        <XCircle size={18} /> Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-zinc-500 mb-6">You don't have an active membership yet. Choose a plan below.</p>
            )}
            <div className="grid md:grid-cols-3 gap-5">
              {plans.map((p, i) => (
                <div key={p.id} className={`rounded-sm p-6 flex flex-col ${p.recommended ? "bg-zinc-950 text-white" : "bg-white border border-zinc-200"}`} data-testid={`dash-plan-${i}`}>
                  <h3 className="font-heading text-xl font-black">{p.name}</h3>
                  <div className="mt-3 flex items-end gap-1">
                    <span className="font-heading text-4xl font-black">${p.price}</span>
                    <span className={`mb-1 text-sm ${p.recommended ? "text-zinc-400" : "text-zinc-500"}`}>/{p.interval}</span>
                  </div>
                  <ul className="mt-4 space-y-2 flex-1">
                    {p.features?.map((f, fi) => (
                      <li key={fi} className="flex items-start gap-2 text-sm">
                        <Check size={16} weight="bold" className="text-[#FF4F00] mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => checkout()} data-testid={`dash-plan-buy-${i}`} className={`mt-6 rounded-sm py-3 font-bold ${p.recommended ? "bg-[#FF4F00] text-white hover:bg-[#E04600]" : "bg-zinc-950 text-white hover:bg-zinc-800"}`}>
                    {sub?.plan_id === p.id ? "Renew" : "Select"}
                  </button>
                </div>
              ))}
            </div>
          </Section>
        )}

        {tab === "book" && (
          <Section title="Book a session" subtitle="Reformer Pilates classes & Styku body scans.">
            <h3 className="font-heading text-xl font-black mb-4">Reformer Pilates classes</h3>
            <div className="grid sm:grid-cols-2 gap-4 mb-10">
              {classes.map((c) => (
                <div key={c.id} className="bg-white border border-zinc-200 rounded-sm p-5 flex items-center justify-between" data-testid={`class-${c.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-[#FF4F00] flex items-center justify-center text-white"><Heartbeat size={20} weight="bold" /></div>
                    <div>
                      <p className="font-bold">{c.title}</p>
                      <p className="text-sm text-zinc-500">{c.day} · {c.time} · {c.coach}</p>
                    </div>
                  </div>
                  <button onClick={() => bookFree("class", c.id, `${c.day} ${c.time}`)} data-testid={`book-class-${c.id}`} className="bg-zinc-950 text-white rounded-sm px-4 py-2 text-sm font-bold hover:bg-zinc-800">Book</button>
                </div>
              ))}
            </div>
            <h3 className="font-heading text-xl font-black mb-4">Services</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {services.map((s) => {
                const Icon = iconFor(s.type);
                const img = serviceImg[s.type];
                return (
                  <div key={s.id} className="bg-white border border-zinc-200 rounded-sm p-5" data-testid={`service-${s.id}`}>
                    {img && (
                      <img src={img} alt={s.name} className="w-full h-40 object-cover object-top rounded-sm mb-4 border border-zinc-100" />
                    )}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-sm bg-zinc-950 flex items-center justify-center text-white"><Icon size={20} weight="bold" /></div>
                      <div>
                        <p className="font-bold">{s.name}</p>
                        <p className="text-sm text-zinc-500">{s.duration} · ${s.price}</p>
                      </div>
                    </div>
                    <p className="text-sm text-zinc-500 mb-4">{s.description}</p>
                    <button onClick={() => window.open(s.type === "pt" ? "https://form.jotform.com/Erin_Milne/form" : "https://otorohangahf.gymmasteronline.com/portal/signup", "_blank", "noopener,noreferrer")} data-testid={`book-service-${s.id}`} className="w-full inline-flex items-center justify-center gap-2 bg-[#FF4F00] text-white rounded-sm py-2.5 font-bold hover:bg-[#E04600]">
                      Book & Pay <ArrowRight size={16} weight="bold" />
                    </button>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {tab === "bookings" && (
          <Section title="My Bookings" subtitle="Everything you've booked.">
            {bookings.length === 0 ? (
              <EmptyState text="No bookings yet. Head to Book to reserve a class." />
            ) : (
              <div className="bg-white border border-zinc-200 rounded-sm divide-y divide-zinc-100">
                {bookings.map((b) => {
                  const cancelled = b.status === "cancelled";
                  return (
                    <div key={b.id} className="p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`booking-${b.id}`}>
                      <div>
                        <p className={`font-bold ${cancelled ? "line-through text-zinc-400" : ""}`}>{b.item_name}</p>
                        <p className="text-sm text-zinc-500">{b.date || new Date(b.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${cancelled ? "text-zinc-500 bg-zinc-100" : "text-green-600 bg-green-50"}`}>{b.status}</span>
                        {!cancelled && b.kind === "class" && (
                          <button onClick={() => setRescheduleFor(b)} data-testid={`reschedule-${b.id}`}
                            className="inline-flex items-center gap-1.5 text-xs font-bold border-2 border-zinc-200 rounded-sm px-3 py-1.5 hover:border-zinc-950">
                            <ArrowsClockwise size={14} weight="bold" /> Reschedule
                          </button>
                        )}
                        {!cancelled && (
                          <button onClick={() => cancelBooking(b)} data-testid={`cancel-booking-${b.id}`}
                            className="inline-flex items-center gap-1.5 text-xs font-bold border-2 border-red-200 text-red-600 rounded-sm px-3 py-1.5 hover:border-red-500">
                            <XCircle size={14} weight="bold" /> Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <Dialog open={!!rescheduleFor} onOpenChange={(o) => !o && setRescheduleFor(null)}>
              <DialogContent className="rounded-sm max-h-[80vh] overflow-y-auto" data-testid="reschedule-dialog">
                <DialogHeader><DialogTitle className="font-heading text-2xl font-black">Reschedule class</DialogTitle></DialogHeader>
                <DialogDescription className="text-sm text-zinc-500 -mt-2">Pick another Reformer Pilates slot with space available.</DialogDescription>
                <div className="space-y-2 mt-2">
                  {classes.filter((c) => c.id !== rescheduleFor?.item_id).map((c) => {
                    const spots = Math.max((c.capacity || 0) - (c.booked_count || 0), 0);
                    const full = (c.capacity || 0) > 0 && spots <= 0;
                    return (
                      <button key={c.id} onClick={() => doReschedule(c.id)} disabled={full} data-testid={`reschedule-option-${c.id}`}
                        className={`w-full flex items-center justify-between border-2 rounded-sm px-4 py-3 text-left transition-colors ${full ? "border-zinc-100 opacity-50 cursor-not-allowed" : "border-zinc-200 hover:border-zinc-950"}`}>
                        <span className="font-bold text-sm">{c.title} · {c.day} {c.time}</span>
                        <span className={`text-xs font-bold ${full ? "text-red-500" : "text-green-600"}`}>{full ? "Full" : `${spots} left`}</span>
                      </button>
                    );
                  })}
                </div>
              </DialogContent>
            </Dialog>
          </Section>
        )}

        {tab === "notifications" && (
          <Section title="Alerts" subtitle="Messages from the gym.">
            {notifications.length === 0 ? (
              <EmptyState text="No alerts right now." />
            ) : (
              <div className="space-y-3">
                {notifications.map((n) => (
                  <div key={n.id} className={`rounded-sm p-4 border ${n.read ? "bg-white border-zinc-200" : "bg-orange-50 border-orange-200"}`} data-testid={`notif-${n.id}`}>
                    <div className="flex items-start gap-3">
                      <Bell size={20} weight="bold" className="text-[#FF4F00] mt-0.5" />
                      <div className="flex-1">
                        <p className="font-bold text-sm">{n.message}</p>
                        <p className="text-xs text-zinc-400 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                      </div>
                      {!n.read && (
                        <button onClick={async () => { await api.post(`/notifications/${n.id}/read`); refresh(); }} className="text-xs font-bold underline">Mark read</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        )}

        {tab === "profile" && <ProfileTab user={user} checkAuth={checkAuth} />}
      </main>
    </div>
  );
}

function ProfileTab({ user, checkAuth }) {
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/profile", form);
      await checkAuth();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section title="Profile" subtitle="Manage your details.">
      <div className="bg-white border border-zinc-200 rounded-sm p-6 max-w-lg space-y-4">
        <div className="space-y-1.5">
          <Label>Name</Label>
          <Input data-testid="profile-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" />
        </div>
        <div className="space-y-1.5">
          <Label>Email</Label>
          <Input value={user?.email || ""} disabled className="rounded-sm bg-zinc-50" />
        </div>
        <div className="space-y-1.5">
          <Label>Phone</Label>
          <Input data-testid="profile-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm" />
        </div>
        <button onClick={save} disabled={busy} data-testid="profile-save" className="bg-zinc-950 text-white rounded-sm px-6 py-3 font-bold hover:bg-zinc-800">
          {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </Section>
  );
}

// ---- shared UI ----
export function Sidebar({ nav, tab, setTab, logout, navigate, name, role, onAccountClick }) {
  return (
    <aside className="hidden md:flex fixed left-0 top-0 h-full w-64 bg-white border-r border-zinc-200 flex-col">
      <div className="p-6 border-b border-zinc-200 shrink-0">
        <button onClick={() => navigate("/")} className="font-heading font-black text-sm tracking-tight text-left">
          OTOROHANGA<span className="text-[#FF4F00]">.</span>
          <span className="block text-[9px] tracking-[0.3em] text-zinc-400">HEALTH & FITNESS</span>
        </button>
      </div>
      <nav className="flex-1 min-h-0 overflow-y-auto p-4 space-y-1">
        {nav.map((n) => (
          <button
            key={n.id}
            onClick={() => setTab(n.id)}
            data-testid={`tab-${n.id}`}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm font-bold text-sm transition-colors ${tab === n.id ? "bg-zinc-950 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}
          >
            <n.icon size={20} weight="bold" />
            <span className="flex-1 text-left">{n.label}</span>
            {n.badge ? <span className="bg-[#FF4F00] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{n.badge}</span> : null}
          </button>
        ))}
      </nav>
      <div className="p-4 border-t border-zinc-200 shrink-0">
        <button
          onClick={onAccountClick}
          data-testid="sidebar-account"
          className="w-full flex items-center gap-3 px-4 py-3 mb-1 rounded-sm text-left hover:bg-zinc-100 transition-colors group"
        >
          <div className="w-9 h-9 rounded-full bg-[#FF4F00] text-white flex items-center justify-center font-black shrink-0">
            {(name || "?").trim().charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-bold text-sm truncate">{name}</p>
            <p className="text-xs text-zinc-400">{role} · Edit profile</p>
          </div>
          <UserCircle size={18} weight="bold" className="text-zinc-300 group-hover:text-[#FF4F00] transition-colors" />
        </button>
        <button onClick={logout} data-testid="logout-btn" className="w-full flex items-center gap-3 px-4 py-3 rounded-sm font-bold text-sm text-zinc-600 hover:bg-zinc-100">
          <SignOut size={20} weight="bold" /> Sign out
        </button>
      </div>
    </aside>
  );
}

export const Section = ({ title, subtitle, children }) => (
  <div>
    <div className="mb-8">
      <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">{title}</h1>
      {subtitle && <p className="text-zinc-500 mt-1">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const StatCard = ({ label, value, sub, highlight, warn }) => (
  <div className={`rounded-sm p-5 border ${warn ? "bg-red-50 border-red-200" : highlight ? "bg-zinc-950 text-white border-zinc-950" : "bg-white border-zinc-200"}`}>
    <p className={`text-xs uppercase tracking-widest font-bold ${highlight ? "text-zinc-400" : "text-zinc-400"}`}>{label}</p>
    <p className="font-heading text-3xl font-black mt-1 capitalize">{value}</p>
    <p className={`text-sm mt-0.5 capitalize ${warn ? "text-red-600" : highlight ? "text-zinc-400" : "text-zinc-500"}`}>{sub}</p>
  </div>
);

const ActionBtn = ({ onClick, icon: Icon, children }) => (
  <button onClick={onClick} className="inline-flex items-center gap-2 border-2 border-zinc-200 rounded-sm px-4 py-2.5 font-bold text-sm hover:border-zinc-950 transition-colors">
    <Icon size={18} weight="bold" /> {children}
  </button>
);

const EmptyState = ({ text }) => (
  <div className="bg-white border border-dashed border-zinc-300 rounded-sm p-12 text-center text-zinc-400 font-bold">{text}</div>
);

const StatusPill = ({ status }) => {
  const map = { active: "text-green-600 bg-green-50", paused: "text-amber-600 bg-amber-50", cancelled: "text-zinc-500 bg-zinc-100", overdue: "text-red-600 bg-red-50" };
  return <span className={`inline-block mt-2 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${map[status] || "bg-zinc-100"}`}>{status}</span>;
};
